import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import Spinner from '../components/ui/Spinner';
import Toggle from '../components/ui/Toggle';
import ResponseConfigForm, { type ResponseConfig } from '../components/ResponseConfigForm';
import { Card, CardBody } from '../components/ui/Card';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useAccounts } from '../contexts/AccountContext';
import {
  listFlows,
  getFlow,
  createFlow,
  updateFlow,
  setFlowEnabled,
  deleteFlow,
  refreshStories,
  type FlowSummary,
  type StoryItem,
} from '../lib/sections-api';

const EMPTY_CONFIG: ResponseConfig = { replyText: '', steps: [], buttons: [] };

export default function StoriesPage() {
  useDocumentTitle('استوری‌ها');
  const { selectedAccountId } = useAccounts();
  const [stories, setStories] = useState<StoryItem[]>([]);
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStoryId, setEditingStoryId] = useState<string | null>(null);
  const [config, setConfig] = useState<ResponseConfig>(EMPTY_CONFIG);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (selectedAccountId === null) return;
    let cancelled = false;
    listFlows('story', selectedAccountId)
      .then((list) => {
        if (!cancelled) setFlows(list);
      })
      .catch(() => {
        if (!cancelled) setFlows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAccountId, reloadKey]);

  const reload = (): void => setReloadKey((k) => k + 1);

  const handleRefresh = async (): Promise<void> => {
    if (selectedAccountId === null) return;
    setRefreshing(true);
    setError(null);
    try {
      const items = await refreshStories(selectedAccountId);
      setStories(items);
    } catch {
      setError('دریافت استوری‌ها از اینستاگرام ممکن نشد. حساب متصل و فعال باشد.');
    } finally {
      setRefreshing(false);
    }
  };

  const openConfig = async (storyMediaId: string): Promise<void> => {
    setEditingStoryId(storyMediaId);
    setError(null);
    const existing = flows.find((f) => f.target_media_id === storyMediaId);
    if (existing !== undefined) {
      try {
        const detail = await getFlow('story', existing.id);
        setConfig({
          replyText: detail.flow.reply_text ?? '',
          steps: detail.steps.map((s) => ({
            kind: s.kind,
            text: s.text,
            media_asset_id: s.media_asset_id,
          })),
          buttons: detail.buttons
            .filter((b) => b.parent_button_id === null)
            .map((b) => ({
              label: b.label,
              action_kind: b.action_kind,
              text: b.text,
              media_asset_id: b.media_asset_id,
              url: b.url,
              children: detail.buttons
                .filter((c) => c.parent_button_id === b.id)
                .map((c) => ({
                  label: c.label,
                  action_kind: c.action_kind,
                  text: c.text,
                  media_asset_id: c.media_asset_id,
                  url: c.url,
                })),
            })),
        });
      } catch {
        setConfig(EMPTY_CONFIG);
      }
    } else {
      setConfig(EMPTY_CONFIG);
    }
    setModalOpen(true);
  };

  const handleSave = async (): Promise<void> => {
    if (selectedAccountId === null || editingStoryId === null) return;
    setSaving(true);
    setError(null);
    const payload = {
      instagram_account_id: selectedAccountId,
      target_media_id: editingStoryId,
      reply_text: config.replyText.trim() === '' ? null : config.replyText,
      steps: config.steps,
      buttons: config.buttons,
    };
    try {
      const existing = flows.find((f) => f.target_media_id === editingStoryId);
      if (existing !== undefined) {
        await updateFlow('story', existing.id, payload);
      } else {
        await createFlow('story', payload);
      }
      setModalOpen(false);
      reload();
    } catch {
      setError('ذخیره پیکربندی ممکن نشد.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (flow: FlowSummary, enabled: boolean): Promise<void> => {
    try {
      await setFlowEnabled('story', flow.id, enabled);
      reload();
    } catch {
      // ignore
    }
  };

  const handleDelete = async (flow: FlowSummary): Promise<void> => {
    try {
      await deleteFlow('story', flow.id);
      reload();
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <PageHeader
        title="استوری‌ها"
        description="پاسخ خودکار به پاسخ کاربران به استوری‌های فعال"
        actions={
          <Button
            onClick={() => void handleRefresh()}
            loading={refreshing}
            variant="secondary"
            size="sm"
          >
            <Icon name="refresh" className="h-4 w-4" />
            بروزرسانی از اینستاگرام
          </Button>
        }
      />

      {error !== null && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700">
          <Icon name="alert" className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {selectedAccountId === null ? (
        <EmptyState
          icon="story"
          title="حسابی انتخاب نشده"
          description="از بالای صفحه یک حساب انتخاب کنید."
        />
      ) : (
        <>
          <Card className="mb-6">
            <CardBody>
              <p className="mb-3 text-xs font-medium text-slate-700">استوری‌های فعال</p>
              {refreshing ? (
                <Spinner className="py-8" />
              ) : stories.length === 0 ? (
                <p className="py-4 text-center text-xs text-slate-400">
                  استوری فعالی یافت نشد. برای دریافت، «بروزرسانی» را بزنید.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                  {stories.map((story) => {
                    const hasFlow = flows.some((f) => f.target_media_id === story.media_id);
                    return (
                      <button
                        key={story.media_id}
                        type="button"
                        onClick={() => void openConfig(story.media_id)}
                        className="group relative aspect-[9/16] overflow-hidden rounded-xl border-2 border-slate-200 transition-all hover:border-brand-400 hover:shadow-md"
                      >
                        {story.thumbnail_url !== null ? (
                          <img
                            src={story.thumbnail_url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-300">
                            <Icon name="story" className="h-8 w-8" />
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                          <p className="truncate text-[10px] text-white">
                            {story.caption ?? story.media_id.slice(0, 10)}
                          </p>
                        </div>
                        {hasFlow && (
                          <div className="absolute end-1.5 top-1.5">
                            <Badge tone="success">فعال</Badge>
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                          <span className="rounded-lg bg-white/90 px-2 py-1 text-[10px] font-semibold text-slate-800 opacity-0 transition-opacity group-hover:opacity-100">
                            پیکربندی
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>

          <p className="mb-3 text-xs font-medium text-slate-700">پیکربندی‌های ذخیره‌شده</p>
          {loading ? (
            <Spinner className="py-8" />
          ) : flows.length === 0 ? (
            <EmptyState
              icon="story"
              title="پیکربندی‌ای وجود ندارد"
              description="روی یک استوری کلیک کنید تا پاسخ خودکار تنظیم کنید."
            />
          ) : (
            <div className="space-y-2">
              {flows.map((flow) => (
                <Card key={flow.id}>
                  <CardBody className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800" dir="ltr">
                        استوری {flow.target_media_id}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-slate-500">
                        {flow.reply_text ?? 'بدون متن پاسخ'}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Toggle checked={flow.enabled} onChange={(v) => void handleToggle(flow, v)} />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void openConfig(flow.target_media_id ?? '')}
                      >
                        ویرایش
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void handleDelete(flow)}>
                        <Icon name="trash" className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`پیکربندی استوری ${editingStoryId ?? ''}`}
        wide
      >
        <ResponseConfigForm value={config} onChange={setConfig} />
        <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            انصراف
          </Button>
          <Button onClick={() => void handleSave()} loading={saving}>
            ذخیره
          </Button>
        </div>
      </Modal>
    </div>
  );
}
